/** Drag & drop overlay for the storage page — hands dropped files to the page (which asks for descriptions) */

import { useState, useEffect, useRef } from 'react'
import { Upload } from 'lucide-react'
import { useT } from '../../i18n'

interface StorageDropZoneProps {
  /** Display name of the folder the files go into */
  folderName: string
  /** Ignore drops (e.g. while a dialog is open) */
  disabled?: boolean
  onDropFiles: (files: File[]) => void
}

const hasFiles = (e: DragEvent) => e.dataTransfer?.types.includes('Files') ?? false

export function StorageDropZone({ folderName, disabled, onDropFiles }: StorageDropZoneProps) {
  const t = useT()
  const [isDragging, setIsDragging] = useState(false)
  const dragCounterRef = useRef(0)
  // Latest callback/flags without re-binding the document listeners on every render
  const onDropRef = useRef(onDropFiles)
  onDropRef.current = onDropFiles
  const disabledRef = useRef(disabled)
  disabledRef.current = disabled

  useEffect(() => {
    const reset = () => {
      dragCounterRef.current = 0
      setIsDragging(false)
    }
    const onDragEnter = (e: DragEvent) => {
      if (!hasFiles(e) || disabledRef.current) return
      e.preventDefault()
      dragCounterRef.current++
      if (dragCounterRef.current === 1) setIsDragging(true)
    }
    const onDragLeave = (e: DragEvent) => {
      if (!hasFiles(e) || disabledRef.current) return
      dragCounterRef.current = Math.max(0, dragCounterRef.current - 1)
      if (dragCounterRef.current === 0) setIsDragging(false)
    }
    const onDragOver = (e: DragEvent) => {
      if (!hasFiles(e) || disabledRef.current) return
      e.preventDefault()
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy'
    }
    const onDrop = (e: DragEvent) => {
      if (!hasFiles(e) || disabledRef.current) {
        reset()
        return
      }
      e.preventDefault()
      reset()
      const files = e.dataTransfer?.files
      if (files?.length) onDropRef.current(Array.from(files))
    }
    document.addEventListener('dragenter', onDragEnter)
    document.addEventListener('dragleave', onDragLeave)
    document.addEventListener('dragover', onDragOver)
    document.addEventListener('drop', onDrop)
    return () => {
      document.removeEventListener('dragenter', onDragEnter)
      document.removeEventListener('dragleave', onDragLeave)
      document.removeEventListener('dragover', onDragOver)
      document.removeEventListener('drop', onDrop)
    }
  }, [])

  if (!isDragging) return null

  return (
    <div className="pointer-events-none fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm" aria-hidden="true">
      <div className="flex flex-col items-center gap-3 rounded-xl border-2 border-dashed border-accent bg-surface-raised px-10 py-12 text-center shadow-2xl">
        <Upload className="h-10 w-10 text-accent-text" />
        <p className="font-display text-lg font-semibold text-fg">{t('storage.drop.title')}</p>
        <p className="text-sm text-fg-muted">{t('storage.drop.hint', { name: folderName })}</p>
      </div>
    </div>
  )
}
